import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { EASE } from "./ui-bits";
import { MAIN_NAV, SITE } from "../data/site";

const MOBILE_LINKS = [
  ...MAIN_NAV,
  { label: "Flower Delivery", href: "/flower-delivery", testid: "nav-delivery" },
  { label: "Holidays & Seasonal", href: "/seasonal", testid: "nav-seasonal" },
  { label: "Gallery", href: "/gallery", testid: "nav-gallery" },
  { label: "Journal", href: "/journal", testid: "nav-journal" },
  { label: "FAQ", href: "/faq", testid: "nav-faq" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 bg-plum transition-all duration-500 ${
          scrolled ? "shadow-[0_10px_36px_rgba(48,28,45,0.45)]" : ""
        }`}
      >
        <div className="gold-rule opacity-40" aria-hidden="true" />
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 transition-all duration-500 sm:px-8 ${
            scrolled ? "py-2" : "py-3.5"
          }`}
        >
          <Link to="/" aria-label="JIF Flowers home" data-testid="header-logo-link" className="shrink-0">
            <span className="inline-flex items-center rounded-full border border-gold/50 bg-ivory px-4 py-1.5 shadow-sm">
              <Logo variant="full" className={`w-auto transition-all duration-500 ${scrolled ? "h-8" : "h-10 sm:h-12"}`} />
            </span>
          </Link>

          <nav aria-label="Main navigation" className="hidden items-center gap-7 lg:flex">
            {MAIN_NAV.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                data-testid={item.testid}
                className={`whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  location.pathname.startsWith(item.href) ? "text-gold" : "text-ivory/85 hover:text-gold"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <motion.a
              href={SITE.phoneHref}
              data-testid="header-call-button"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className="hidden items-center gap-2 whitespace-nowrap rounded-full bg-sage px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.12em] text-ivory transition-colors duration-300 hover:bg-[#57603A] sm:inline-flex"
            >
              <Phone className="h-4 w-4" strokeWidth={2.4} aria-hidden="true" />
              {SITE.phone}
            </motion.a>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
              className="flex h-10 w-10 items-center justify-center rounded-full text-ivory transition-colors hover:text-gold lg:hidden"
            >
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-aubergine"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="inline-flex items-center rounded-full border border-gold/50 bg-ivory px-4 py-1.5">
                <Logo variant="full" className="h-8 w-auto" />
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center rounded-full text-ivory hover:text-gold"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <nav aria-label="Mobile navigation" className="flex flex-1 flex-col justify-center gap-1 overflow-y-auto px-8 py-6">
              {MOBILE_LINKS.map((item, i) => (
                <motion.div
                  key={item.href}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 + i * 0.045, duration: 0.5, ease: EASE }}
                >
                  <Link
                    to={item.href}
                    data-testid={`mobile-${item.testid}`}
                    className="block border-b border-gold/15 py-3 font-serif text-2xl font-medium text-ivory transition-colors hover:text-gold"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5, ease: EASE }}
                className="mt-8"
              >
                <a
                  href={SITE.phoneHref}
                  data-testid="mobile-menu-call-button"
                  className="flex items-center justify-center gap-2.5 rounded-full bg-gold px-7 py-4 text-sm font-bold uppercase tracking-[0.14em] text-plum"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  Call to Order: {SITE.phone}
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
